import { useEffect, useState } from 'react';
import { compose } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import Error from 'core/errors/Error';
import withAppState from 'hoc/withAppState';
import PageContent from 'layouts/PageContent';
import Transaction from 'components/transactions/Transaction';

import { buildRoute } from 'routes';
import Button from 'core/buttons/Button';
import {
  resetGetTransactionFailure,
  resetUpdateRejectedToCompleted,
} from 'app/features/transactions/transactionSlice';
import withRole from 'hoc/withRole';
import acl from 'app/config/acl';
import { Box, Grid } from '@mui/material';
import ViewTransactionDocument from './ViewTransactionDocument';
import ViewTransactionDetailDocument from './ViewTransactionDetailDocument';
import SpringModal from 'core/modal/SpringModal';
import UpdateRejectedToCompletedForm from 'components/transactions/UpdateRejectedToCompletedForm';
import {
  updateRejectedToCompleted,
  getTransaction,
  getTransactions,
} from 'app/features/transactions/transactionSaga';
import { getTransactionsLog } from 'app/features/transactions/transactionLogSaga';
import { transactionStatuses } from 'app/config/TransactionStatus';

function ViewTransaction({ isNetworkError }) {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    isError,
    errorCode,
    errorMessage,
    isLoading,
    data: transaction,
  } = useSelector((state) => state.transactions.show);

  const { isLoading: isUpdating, isSuccess: isUpdateSuccess } = useSelector(
    (state) => state.transactions.updateRejectedToCompleted,
  );

  const { params: transactionListParams } = useSelector((state) => state.transactions.index);

  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (isError || isNetworkError) {
      dispatch(resetGetTransactionFailure());
    }
  }, [isNetworkError, isError, dispatch]);

  useEffect(() => {
    if (isUpdateSuccess && !isUpdating) {
      setOpenModal(false);
      dispatch(getTransaction(params.transactionId));
      dispatch(getTransactionsLog(params.transactionId));
      if (transactionListParams) {
        dispatch(getTransactions(transactionListParams));
      }
      dispatch(resetUpdateRejectedToCompleted());
    }
  }, [isUpdateSuccess, isUpdating, dispatch, params.transactionId, transactionListParams]);

  const [customerId, setCustomerId] = useState(null);
  const handleOpenModal = (e) => {
    e.preventDefault();
    dispatch(resetUpdateRejectedToCompleted());
    setOpenModal(true);
  };

  const handleSubmit = (data) => {
    dispatch(
      updateRejectedToCompleted({
        ...data,
        transactionId: transaction?.order_id,
      }),
    );
  };

  if (isError)
    return (
      <Error
        status={errorCode}
        message={errorMessage}
        onRetry={() => dispatch(resetGetTransactionFailure())}
      />
    );

  const isRejected =
    transaction?.status?.toLowerCase().trim() === transactionStatuses.rejected.toLowerCase().trim();

  return (
    <PageContent
      title="Transaction Details"
      rightTopEndContent={
        <Box display="flex" gap={2}>
          {!isLoading && transaction && isRejected && (
            <Button color="primary" type="button" onClick={handleOpenModal}>
              Update Status
            </Button>
          )}
          <Button
            onClick={() =>
              navigate(buildRoute('customers.show', customerId), {
                state: { activeTab: 'customer-transactions' },
              })
            }
          >
            History
          </Button>{' '}
          <Button onClick={() => navigate(buildRoute('transactions.index'))}>
            All Transaction
          </Button>
        </Box>
      }
    >
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          md={9}
          sx={{
            overflowY: {
              xs: 'visible',
              md: 'auto',
            },
            maxHeight: '100vh',
          }}
        >
          <Transaction transactionId={params.transactionId} />
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
          sx={{
            position: 'sticky',
            top: 16, // distance from top
            height: 'calc(100vh - 32px)',
            overflowY: 'auto',
            paddingLeft: 2,
          }}
        >
          <ViewTransactionDetailDocument
            transactionId={params.transactionId}
            onCustomerId={setCustomerId}
          />
        </Grid>
      </Grid>

      <SpringModal
        header="Update Transaction Status"
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        enableMaximizeButton={false}
        enableDefaultPadding
        sx={{ maxWidth: '600px' }}
      >
        <UpdateRejectedToCompletedForm
          onSubmit={handleSubmit}
          onCancel={() => setOpenModal(false)}
          isSubmitting={isUpdating}
        />
      </SpringModal>
    </PageContent>
  );
}

export default compose(
  withAppState,
  withRole([acl.SUPERADMIN, acl.ADMIN, acl.USER]),
)(ViewTransaction);
